import { ChatPanel } from "llmasaservice-ui";
import { useUser } from "../contexts/useUser";
import "../../node_modules/llmasaservice-ui/dist/index.css";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMGroups } from "../dataaccess/fetchLLMGroups";
import { fetchLLMModels } from "../dataaccess/fetchLLMModels";
import { useEffect, useState } from "react";
import { Checkbox } from "@mui/material";
import { LLMEmailCaptureMode } from "../API";
import { useNavigate, useSearchParams } from "react-router-dom";
const ChatTest: React.FC = () => {
  const { project } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [createConversation, setCreateConversation] = useState<boolean>(false);
  const [customerEmailCaptureMode, setCustomerEmailCaptureMode] =
    useState<LLMEmailCaptureMode>(LLMEmailCaptureMode.HIDE);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchLLMGroups(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  const { data: modelsData, isLoading: modelsLoading, isError: modelsError } = useQuery({
    queryKey: ["models"],
    queryFn: () => fetchLLMModels(),
    enabled: true,
  });

  useEffect(() => {
    const requestedService = searchParams.get("service") || "";
    if (requestedService) {
      setSelectedGroup(requestedService);
    }
  }, [searchParams]);

  if (isLoading || modelsLoading) {
    return <div>Loading...</div>;
  }

  if (isError || modelsError) {
    return <div>Error...</div>;
  }

  const groups = data?.data?.groupsByProjectId?.items ?? [];
  const models = modelsData?.data?.listLLMModels?.items ?? [];

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }
  const THEME_KEY = "_llmasaservice_theme";
  const userThemePreference = localStorage.getItem(THEME_KEY) || "dark";

  // Group models by their default service group and vendor
  const modelsByServiceGroup = models.reduce((acc, model) => {
    const serviceGroup = model?.defaultServiceGroup || "Other";
    if (!acc[serviceGroup]) {
      acc[serviceGroup] = [];
    }
    acc[serviceGroup].push(model);
    return acc;
  }, {} as Record<string, any[]>);

  const modelsByVendor = models.reduce((acc, model) => {
    const vendor = model?.vendor || "Unknown";
    if (!acc[vendor]) {
      acc[vendor] = [];
    }
    acc[vendor].push(model);
    return acc;
  }, {} as Record<string, any[]>);

  const options = [
    { id: "", label: "Default group", type: "default" },
    
    // Service Groups
    { id: "group:standard", label: "Standard Model Group", type: "service-group" },
    { id: "group:premium", label: "Premium Model Group", type: "service-group" },
    { id: "group:reasoning", label: "Reasoning Model Group", type: "service-group" },
    { id: "group:superpremium", label: "Super Premium Model Group", type: "service-group" },
    { id: "group:supersmall", label: "Super Small Model Group", type: "service-group" },
    { id: "openrouter:standard", label: "OpenRouter:Standard Model Group", type: "service-group" },
    { id: "openrouter:premium", label: "OpenRouter:Premium Model Group", type: "service-group" },

    // Custom Groups
    ...(groups?.map((group) => ({
      id: group?.id ?? "",
      label: "Custom group: " + (group?.name ?? ""),
      type: "custom-group"
    })) ?? []),
    
    // Separator for Models by Service Group
    { id: "separator-service", label: "— Models by Service Group —", type: "separator", disabled: true },
    
    // Models grouped by Service Group
    ...Object.entries(modelsByServiceGroup)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([serviceGroup, groupModels]) => [
        // Service group header
        { 
          id: `header-service-${serviceGroup}`, 
          label: `${serviceGroup} Models`, 
          type: "header", 
          disabled: true 
        },        // Models in this service group
        ...groupModels
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
          .map((model) => ({
            id: `model:${model?.id}`,
            label: `  ${model?.name} (${model?.vendor}${model?.host && model?.host !== model?.vendor ? ` via ${model?.host}` : ""})`,
            type: "model"
          }))
      ]),
    
    // Separator for Models by Vendor
    { id: "separator-vendor", label: "— Models by Vendor —", type: "separator", disabled: true },
    
    // Models grouped by Vendor
    ...Object.entries(modelsByVendor)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([vendor, vendorModels]) => [
        // Vendor header
        { 
          id: `header-vendor-${vendor}`, 
          label: `${vendor} Models`, 
          type: "header", 
          disabled: true 
        },        // Models from this vendor
        ...vendorModels
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
          .map((model) => ({
            id: `model:${model?.id}`,
            label: `  ${model?.name}${model?.host && model?.host !== model?.vendor ? ` (via ${model?.host})` : ""}`,
            type: "model"
          }))
      ])
  ];

  const hasSelectedGroupInOptions = options.some((option) => option.id === selectedGroup);
  const selectedGroupLabel =
    selectedGroup && selectedGroup.startsWith("model:")
      ? `Query model: ${selectedGroup.replace("model:", "")}`
      : selectedGroup;
  const renderedOptions =
    selectedGroup && !hasSelectedGroupInOptions
      ? [
          {
            id: selectedGroup,
            label: selectedGroupLabel,
            type: "query-model",
          },
          ...options,
        ]
      : options;

  return (
    <Box margin={2}>
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => navigate("/chat/batch") }>
            Open Batch Test
          </Button>
        }
      >
        This page is for normal chat testing. To test async batch calls and watch the call record timeline, open Batch Test.
      </Alert>

      <Paper
        elevation={3}
        style={{ borderRadius: 3, border: "1px solid #444" }}
      >
        <Box padding={2} margin={1}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(320px, 1.2fr) auto auto" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="group-select-label">Service Group</InputLabel>
              <Select
                labelId="group-select-label"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                label="Service Group"
                size="small"
              >
                {renderedOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    disabled={option.disabled}
                    style={{
                      fontWeight: option.type === "header" ? "bold" : "normal",
                      fontSize:
                        option.type === "header"
                          ? "0.9em"
                          : option.type === "separator"
                          ? "0.85em"
                          : "1em",
                      color:
                        option.type === "separator" || option.type === "header"
                          ? "#666"
                          : "inherit",
                      backgroundColor:
                        option.type === "separator" || option.type === "header"
                          ? "#f5f5f5"
                          : "inherit",
                      paddingLeft: option.type === "model" ? "32px" : "16px",
                      fontStyle: option.type === "separator" ? "italic" : "normal",
                      cursor: option.disabled ? "default" : "pointer",
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={createConversation}
                  onChange={(e) => setCreateConversation(e.target.checked)}
                />
              }
              label="Save conversation?"
            />

            <FormControl sx={{ minWidth: { xs: "100%", md: 260 } }}>
              <InputLabel id="email-capture-label">Email capture mode</InputLabel>
              <Select
                labelId="email-capture-label"
                value={customerEmailCaptureMode}
                onChange={(e: any) => setCustomerEmailCaptureMode(e.target.value)}
                label="Email capture mode"
                size="small"
              >
                <MenuItem value={LLMEmailCaptureMode.HIDE}>Hide</MenuItem>
                <MenuItem value={LLMEmailCaptureMode.OPTIONAL}>Optional</MenuItem>
                <MenuItem value={LLMEmailCaptureMode.REQUIRED}>Required</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {selectedGroup && (
            <Box sx={{ mt: 1.25, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Active test target
              </Typography>
              <Chip size="small" label={selectedGroup} variant="outlined" />
            </Box>
          )}
        </Box>
      </Paper>

      <ChatPanel
        project_id={project?.id ?? ""}
        initialMessage={`Hello, use this chat to test the LLM services defined in this project. Tokens are used from the customer called ${project?.name}.`}
        theme={userThemePreference === "light" ? "light" : "dark"}
        height="80vh"
        width="100%"
        url={url}
        hideInitialPrompt={false}
        service={selectedGroup}
        createConversationOnFirstChat={createConversation}
        customerEmailCaptureMode={customerEmailCaptureMode}
      />
    </Box>
  );
};

export default ChatTest;
